import { loadConfig } from './config';

/**
 * In-memory cache of GLB blob URLs, keyed by the model's public path
 * (e.g. `/assets/models/9136.glb?v=1758115333`).
 *
 * Every `<model-viewer>` mounted in the Control UI would otherwise
 * kick off its own HTTP request for the same GLB — even with the
 * browser HTTP cache, the roundtrip + parse can be tens of ms per
 * mount and mobile devices show visible re-download flashes.
 *
 * Here we:
 *   1. De-duplicate concurrent requests via a pending-Promise map
 *   2. Store the resulting Blob URL for the lifetime of the tab
 *      (revoking is unnecessary; these are small and finite)
 *
 * Freshness comes from the URL: the sync server appends `?v=<mtime>`
 * to every model path it hands out, so an updated .glb produces a
 * different key here and re-downloads automatically.
 */
const cache = new Map<string, string>();
const pending = new Map<string, Promise<string>>();

/**
 * Resolve a model path to a cached blob URL suitable to pass straight
 * into `<model-viewer src>`. Downloads on first call, replay from
 * memory on every subsequent call.
 */
export async function getCachedModelURL(modelPath: string): Promise<string> {
  const cached = cache.get(modelPath);
  if (cached) return cached;
  const inFlight = pending.get(modelPath);
  if (inFlight) return inFlight;

  const promise = (async () => {
    const cfg = await loadConfig();
    const p = modelPath.startsWith('/') ? modelPath : `/${modelPath}`;
    const url = `${cfg.syncBaseURL}${p}`;
    // Respect the sync server's Cache-Control (no-cache + ETag) so an
    // updated .glb served under the same URL is still revalidated.
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`Failed to load ${modelPath}: HTTP ${res.status}`);
    const blob = await res.blob();
    const objectURL = URL.createObjectURL(blob);
    cache.set(modelPath, objectURL);
    pending.delete(modelPath);
    return objectURL;
  })();

  pending.set(modelPath, promise);
  return promise;
}

/** Debug/inspection helpers (unused at runtime; handy from devtools). */
export function _modelCacheSize() {
  return cache.size;
}

// ---------------------------------------------------------------------
// Concurrent <model-viewer> slot limiter
// ---------------------------------------------------------------------
//
// Every live <model-viewer> allocates a WebGL context + parsed glTF
// scene. Beyond a couple of dozen the tab OOMs. This is a global
// counted semaphore so even if an IntersectionObserver misfires (e.g.
// during a Radix dialog open animation) we never mount more than
// MAX_ACTIVE_VIEWERS at once. Tiles waiting on a slot render as a
// static placeholder; when a slot frees up all waiters retry FIFO.

const MAX_ACTIVE_VIEWERS = 32;
let activeViewers = 0;
const waiters = new Set<() => void>();

/** Try to reserve a viewer slot. Returns true on success. */
export function tryClaimViewerSlot(): boolean {
  if (activeViewers < MAX_ACTIVE_VIEWERS) {
    activeViewers++;
    return true;
  }
  return false;
}

/** Release a previously-claimed slot and notify waiters. */
export function releaseViewerSlot(): void {
  if (activeViewers > 0) activeViewers--;
  // Snapshot to avoid iteration mutation issues; each waiter tries
  // to claim; whichever get slots claim them, the rest stay queued.
  Array.from(waiters).forEach((cb) => cb());
}

/**
 * Register a callback that runs whenever a slot may have opened up.
 * Returns an unsubscribe function.
 */
export function onViewerSlotAvailable(cb: () => void): () => void {
  waiters.add(cb);
  return () => {
    waiters.delete(cb);
  };
}
